import { IS_DEV, IS_PROD, IS_QA, IS_UAT } from "@/config/constants";
import type { FrameName } from "@/game/entities/config";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useSocketService from "@/hooks/useSocketService";
import useUserStore from "@/store/useUserStore";
import { ref } from "vue";
import { version } from "../../package.json";

const CONSOLE_COLOR = [
	"%c TEXTS ",
	"background-color:gold;color:black;font-weight:700;",
];

export type AvailableTranslation = {
	code: string;
	name: string;
	percent_completed: number;
};

export type AvailableTranslations = Array<AvailableTranslation>;

const translations = ref<Record<string, string>>({});
const fallbackEnTranslations = ref<Record<string, string>>({});
const availableLanguages = ref<AvailableTranslations>([]);
const currentLanguage = ref<string>("en");

export class Translator {
	private initialized = false;

	private static instance: Translator | null = null;

	public static getInstance(): Translator {
		if (!this.instance) this.instance = new this();
		return this.instance;
	}

	public isInitialized(): boolean {
		return this.initialized;
	}

	private clearLanguagesInLocalStorage(): void {
		Object.keys(localStorage).forEach((key: string): void => {
			if (!key.startsWith("language.")) return;
			localStorage.removeItem(key);
		});
	}

	public async init(): Promise<void> {
		this.initialized = true;

		const fallbackLang = "en";
		const userLang = useUserStore().language;
		currentLanguage.value = userLang;

		const storedVersion = localStorage.getItem("language.version");
		if (storedVersion === version) {
			const storedLanguages = JSON.parse(
				localStorage.getItem("language.list") || "[]",
			);
			const storedFallbackTranslations = JSON.parse(
				localStorage.getItem(`language.${fallbackLang}`) || "{}",
			);
			const storedUserTranslations = JSON.parse(
				localStorage.getItem(`language.${userLang}`) || "{}",
			);

			if (
				storedLanguages.length > 0 &&
				Object.keys(storedFallbackTranslations).length > 0 &&
				(userLang === fallbackLang ||
					Object.keys(storedUserTranslations).length > 0)
			) {
				availableLanguages.value = storedLanguages;
				fallbackEnTranslations.value = storedFallbackTranslations;
				translations.value =
					userLang === fallbackLang ? {} : storedUserTranslations;
				return;
			} else {
				this.clearLanguagesInLocalStorage();
			}
		} else {
			this.clearLanguagesInLocalStorage();
		}

		const socketService = useSocketService();
		await socketService.connect();

		const responseLang = await socketService.initLanguageListHandler();
		availableLanguages.value = responseLang.languages;

		const canStoreLanguagesInLocalStorage =
			(IS_UAT || IS_PROD) &&
			responseLang.languages.length > 0 &&
			!!responseLang.languages.find(
				({ code, percent_completed }) =>
					code === fallbackLang && percent_completed === 100,
			) &&
			!!responseLang.languages.find(
				({ code, percent_completed }) =>
					code === userLang && percent_completed === 100,
			);
		if (canStoreLanguagesInLocalStorage) {
			localStorage.setItem(
				"language.list",
				JSON.stringify(responseLang.languages),
			);
		} else {
			this.clearLanguagesInLocalStorage();
		}

		// Fetch both translations in parallel for better performance
		const [fallbackResult, userResult] = await Promise.all([
			socketService.fetchTranslations({ language: fallbackLang }),
			userLang !== fallbackLang
				? socketService.fetchTranslations({ language: userLang })
				: Promise.resolve({ data: {} }),
		]);

		if ("data" in fallbackResult) {
			fallbackEnTranslations.value = fallbackResult.data;

			if (canStoreLanguagesInLocalStorage) {
				localStorage.setItem(
					`language.${fallbackLang}`,
					JSON.stringify(fallbackResult.data),
				);
			}
		}

		if ("data" in userResult) {
			translations.value = userResult.data;

			if (canStoreLanguagesInLocalStorage) {
				localStorage.setItem(
					`language.${userLang}`,
					JSON.stringify(userResult.data),
				);
			}
		}

		if (canStoreLanguagesInLocalStorage) {
			localStorage.setItem("language.version", version);
		}
	}

	public getAvailableTranslations(): AvailableTranslations {
		return availableLanguages.value.sort((a, b) => {
			return a.name.localeCompare(b.name);
		});
	}

	public get(key: string): string {
		// 1. Try loaded translations (but only if non-empty)
		if (translations.value[key] && translations.value[key].trim() !== "") {
			const translation = translations.value[key];
			return translation.replace(/<!--.*?-->/gs, "");
		}

		// 2. Fallback to English from server
		if (
			fallbackEnTranslations.value[key] &&
			fallbackEnTranslations.value[key].trim() !== ""
		) {
			if (IS_DEV || IS_QA) {
				// eslint-disable-next-line no-console
				console.warn(
					...CONSOLE_COLOR,
					`Fallback EN translation for key ${key} used!`,
				);
			}
			return fallbackEnTranslations.value[key].replace(
				/<!--.*?-->/gs,
				"",
			);
		}
		// 3. Not found
		if (IS_DEV || IS_QA) {
			// eslint-disable-next-line no-console
			console.warn(
				...CONSOLE_COLOR,
				`Translation for key ${key} not found!`,
			);
		}
		return "";
	}

	public async changeLanguage(language: string): Promise<boolean> {
		const socketService = useSocketService();
		const result = await socketService.fetchTranslations({
			language,
		});
		if ("data" in result) {
			translations.value = result.data;
			currentLanguage.value = language;
			useEmitter().emit(EmitterEvents.TranslationsChanged);
			return true;
		} else {
			return false;
		}
	}
}

export const initTranslations = async (): Promise<void> => {
	const translator = Translator.getInstance();
	await translator.init();
	if (IS_DEV || IS_QA) {
		// eslint-disable-next-line no-console
		console.log(
			...CONSOLE_COLOR,
			"Available translations:",
			translator.getAvailableTranslations(),
		);
	}
};

export const translate = (key: string, useKeyAsFallback = false): string => {
	const value = Translator.getInstance().get(key);

	if (value) {
		return value;
	}

	if (useKeyAsFallback) {
		return key;
	}

	return IS_DEV || IS_QA ? `[missing] ${key}` : "";
};

export const canTranslate = (key: string): boolean => {
	return (
		(key in translations.value && translations.value[key].trim() !== "") ||
		(key in fallbackEnTranslations.value &&
			fallbackEnTranslations.value[key].trim() !== "")
	);
};

export const sortFrameNameByTranslation = (
	a: FrameName,
	b: FrameName,
): number => {
	const aTranslation = translate(a + ".name");
	const bTranslation = translate(b + ".name");
	return aTranslation.localeCompare(bTranslation);
};
